import { RefObject, useCallback, useEffect, useRef, useState } from "react";
import { Parameters } from "../pages/Home/Home";
import { getI18n, useTranslation } from "react-i18next";
import {
  CORE,
  ITranslation,
  LANGUAGE_TRANSLATION,
} from "../models/SurveyModels";
import { getStorageItem } from "./localStorage";
export const regexEmail = `/^(([^<>()[]\\.,;:s@"]+(.[^<>()[]\\.,;:s@"]+)*)|(".+"))@(([[0-9]{1,3}.[0-9]{1,3}.[0-9]{1,3}.[0-9]{1,3}])|(([a-zA-Z-0-9]+.)+[a-zA-Z]{2,}))$/`;

export const isEmailValid = (email: string | undefined) =>
  email && email.match(regexEmail);

export const ObjectToClassString = (obj: Record<string, any>) =>
  Object.entries(obj)
    .filter(([key, value]) => Boolean(value))
    .map(([key, value]) => key)
    .join(" ");

export const useComponentVisible = <T extends HTMLElement = HTMLDivElement>(
  initial: boolean
) => {
  const [isComponentVisible, setIsComponentVisible] = useState(initial);
  const ref = useRef<T>(null);

  const handleClickOutside = (event: MouseEvent) => {
    if (ref.current && !ref.current.contains(event.target as Node)) {
      setIsComponentVisible(false);
    }
  };
  useEffect(() => {
    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  });

  return { ref, isComponentVisible, setIsComponentVisible };
};
export const useComponentVisibles = (
  initial: boolean,
  refs: React.MutableRefObject<(HTMLDivElement | null)[]>,
  callback?: () => void
) => {
  const [isComponentVisible, setIsComponentVisible] = useState<boolean[]>(
    Array(refs.current.length).fill(initial)
  );
  const handleClickOutside = (event: MouseEvent) => {
    refs.current.forEach((ref, index) => {
      if (ref && !ref.contains(event.target as Node)) {
        setIsComponentVisible((prevState) => {
          const newState = [...prevState];
          newState[index] = false;
          callback && callback();
          return newState;
        });
      }
    });
  };
  useEffect(() => {
    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  });

  const setComponentVisible = (index: number) => {
    setIsComponentVisible((prevState) => {
      const newState = [...prevState];
      newState[index] = !prevState[index];
      return newState;
    });
  };

  return { isComponentVisible, setComponentVisible };
};
export const setHeaderRoots = (pathSplitted: string[]) =>
  pathSplitted.reduce(
    (acc, arr, index, array) => {
      const addDetail = (
        detail: "" | "template" | "questions"
      ): ["template" | "" | "questions"] | [] =>
        array[index + 1] &&
        array[index + 1].length === 24 &&
        array[index + 2] !== "edit" &&
        detail
          ? [detail]
          : [];
      return (
        {
          companies: {
            parameters: [
              ...acc.parameters,
              "companies.companies",
              ...addDetail(""),
            ] as Parameters[],
            link: "companies",
            icon: "icon-company",
          },
          edit: {
            ...acc,
            parameters: [
              ...acc.parameters,
              array.find((item) => item === "template")
                ? "survey.template.edit"
                : array.find((item) => item === "questions")
                ? "survey.questions.edit"
                : "edit",
            ] as Parameters[],
          },
          create: {
            ...acc,
            parameters: [
              ...acc.parameters,
              array.find((item) => item === "template")
                ? "survey.create.template"
                : array.find((item) => item === "questions")
                ? "survey.questions.create"
                : array.find((item) => item === "library")
                ? "library.create"
                : array.find((item) => item === "issue")
                ? "issue.create"
                : "companies.newCompany",
            ] as Parameters[],
          },
          survey: {
            ...acc,
            parameters: [...acc.parameters, "survey.survey"] as Parameters[],
            link: "survey",
            icon: "icon-clipboard",
          },
          template: {
            ...acc,
            parameters: [
              ...acc.parameters,
              "survey.template.title",
            ] as Parameters[],
          },
          questions: {
            ...acc,
            parameters: [
              ...acc.parameters,
              "survey.questions.title",
            ] as Parameters[],
          },
          library: {
            parameters: [
              ...acc.parameters,
              "library.library",
              ...addDetail(""),
            ] as Parameters[],
            link: "library",
            icon: "icon-book-open",
          },
          issue: {
            parameters: [
              ...acc.parameters,
              "issue.issue",
              ...addDetail(""),
            ] as Parameters[],
            link: "issue",
            icon: "icon-issue",
          },
        }[arr] || acc
      );
    },
    { parameters: [], link: "", icon: "" } as {
      parameters: Parameters[];
      link: string;
      icon: string | JSX.Element;
    }
  );

export const getLocalTranslation = (value: ITranslation[]) => {
  var currentLanguage = getStorageItem("language");
  var languageCurrent: string;
  if (currentLanguage === "it") {
    languageCurrent = LANGUAGE_TRANSLATION.ITALIAN;
  }
  if (currentLanguage === "en") {
    languageCurrent = LANGUAGE_TRANSLATION.ENGLISH;
  }
  const languageObject = value.find(({ language }) => {
    return language === languageCurrent;
  });

  return languageObject === undefined ? value[0] : languageObject;
};

export const filteredArrayFromCurrentLanguage = (
  translations: ITranslation[]
) =>
  translations.filter(
    ({ language }) => language !== getLocalTranslation(translations).language
  );

export function isEqual(obj1: any, obj2: any) {
  if (typeof obj1 !== "object" || typeof obj2 !== "object") {
    return false;
  }

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) {
    return false;
  }

  for (let key of keys1) {
    if (!obj2.hasOwnProperty(key) || obj1[key] !== obj2[key]) {
      return false;
    }
  }

  return true;
}

export function compareObject(obj1: any, obj2: any): boolean {
  if (typeof obj1 !== "object" || typeof obj2 !== "object") {
    return false;
  }
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);
  if (keys1.length !== keys2.length) {
    return false;
  }
  for (const key of keys1) {
    const value1 = obj1[key];
    const value2 = obj2[key];
    if (typeof value1 === "object" && typeof value2 === "object") {
      if (!compareObject(value1, value2)) {
        return false;
      }
    } else if (value1 !== value2) {
      return false;
    }
  }
  return true;
}

export const downloadResponseAsFile = async (
  response: Response,
  filename: string
) => {
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
  a.remove();
};
